import React, { useMemo, useState } from "react";
import { STATUS_META, STATUSES, ENTITY_META, ENTITY_TYPES, OUR_SIDE } from "@/lib/constants";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Sector,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { Users, TrendingUp, HeartHandshake } from "lucide-react";
import CountUp from "@/components/CountUp";

/** Bir durumun firma/şahıs kırılımını verir; alan yoksa sıfırlarla döner. */
const kirilim = (stats, durum) =>
  stats?.by_type?.[durum] || { Firma: 0, Şahıs: 0 };

const CHART_ORDER = STATUSES;

// Active shape: sadece dilimi büyüt + yumuşak dış halka. Harici etiket/çizgi YOK,
// böylece grafik hiçbir zaman kırpılmaz.
const renderActiveShape = (props) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle, payload, fill,
  } = props;
  const color = payload?.color || fill;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        cornerRadius={6}
        fill={color}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 9}
        outerRadius={outerRadius + 13}
        fill={color}
        opacity={0.35}
      />
    </g>
  );
};

export default function StatsPanel({ stats }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const total = stats?.total || 0;

  const chartData = CHART_ORDER
    .map((s) => ({
      name: s,
      label: STATUS_META[s].label,
      desc: STATUS_META[s].desc,
      value: stats?.[s] || 0,
      color: STATUS_META[s].dot,
    }))
    .filter((d) => d.value > 0);

  const active = activeIndex !== null ? chartData[activeIndex] : null;

  // Bizim taraf: yetki belgesi alınanlar + onay/söz alınanlar
  const ourSide = useMemo(() => {
    let toplam = 0, firma = 0, sahis = 0;
    const durumlar = {};
    for (const s of OUR_SIDE) {
      const adet = stats?.[s] || 0;
      const k = kirilim(stats, s);
      toplam += adet;
      firma += k.Firma;
      sahis += k["Şahıs"];
      durumlar[s] = adet;
    }
    return { toplam, firma, sahis, durumlar };
  }, [stats]);

  return (
    <div className="flex flex-col gap-3" data-testid="stats-panel">
      {/* Bizim taraf: yetki belgesi + onay/söz alınanların toplamı */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="order-2 lg:order-first relative overflow-hidden rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-gradient-to-br from-emerald-50 via-white to-white dark:from-emerald-950/50 dark:via-neutral-900 dark:to-neutral-900 p-4 sm:p-5"
        data-testid="stat-our-side"
      >
        <div className="absolute -right-8 -top-10 h-32 w-32 rounded-full bg-emerald-400/20 blur-3xl pointer-events-none" />

        <div className="relative flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-emerald-700 dark:text-emerald-400">
              <HeartHandshake className="h-3 w-3" />
              Bizim Taraf
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span
                className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-emerald-900 dark:text-emerald-200"
                data-testid="stat-our-side-total"
              >
                <CountUp value={ourSide.toplam} />
              </span>
              <span className="text-sm font-mono text-emerald-700/70 dark:text-emerald-400/70">
                / {total} üye
              </span>
            </div>
            <div className="text-[11px] text-emerald-800/70 dark:text-emerald-300/70 mt-0.5">
              Yetki belgesi ve onay/söz alınanların toplamı ·{" "}
              <span className="font-mono font-semibold">
                %{total > 0 ? ((ourSide.toplam / total) * 100).toFixed(1) : "0.0"}
              </span>
            </div>
          </div>

          {/* Kırılımlar: durum ve şirket türü — mobilde 2x2, geniş ekranda tek sıra */}
          <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:flex-wrap">
            {OUR_SIDE.map((s) => (
              <div
                key={s}
                className="rounded-lg border border-emerald-200/70 dark:border-emerald-900/60 bg-white/70 dark:bg-neutral-900/60 px-3 py-2 min-w-0 sm:min-w-[6.5rem]"
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ background: STATUS_META[s].dot }}
                  />
                  <span className="text-[9.5px] uppercase tracking-[0.08em] font-semibold text-neutral-500 dark:text-neutral-400">
                    {STATUS_META[s].short}
                  </span>
                </div>
                <div className="font-heading text-xl font-semibold font-mono text-neutral-900 dark:text-neutral-100 mt-0.5">
                  {ourSide.durumlar[s]}
                </div>
              </div>
            ))}
            {/* MTP'de OUR_SIDE iki elemanlı: şirket ve şahıs ayrı kutularda
                kalınca toplam 4 kutu olur ve mobilde ızgara 2x2 tam kapanır. */}
            {ENTITY_TYPES.map((t) => {
              const em = ENTITY_META[t];
              const adet = t === "Firma" ? ourSide.firma : ourSide.sahis;
              return (
                <div
                  key={t}
                  className={`rounded-lg border ${em.border} ${em.bg} px-3 py-2 min-w-0 sm:min-w-[6.5rem]`}
                  data-testid={`our-side-${em.key}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{ background: em.dot }}
                    />
                    <span className={`text-[9.5px] uppercase tracking-[0.08em] font-semibold ${em.text}`}>
                      {em.label}
                    </span>
                  </div>
                  <div className={`font-heading text-xl font-semibold font-mono mt-0.5 ${em.text}`}>
                    {adet}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Toplam içindeki payı */}
        <div className="relative mt-3 h-2 rounded-full bg-emerald-100 dark:bg-emerald-950/60 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${total > 0 ? (ourSide.toplam / total) * 100 : 0}%` }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400"
          />
        </div>
      </motion.div>

      {/* KPI row */}
      <div className="order-2 lg:order-1 hidden sm:grid sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-2">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="col-span-1 bg-neutral-950 dark:bg-neutral-900 dark:border dark:border-neutral-800 text-white rounded-lg p-4 flex flex-col justify-between min-h-[92px] sm:min-h-[100px] relative overflow-hidden"
        >
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/5 blur-2xl" />
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-400 relative">
            <Users className="h-3 w-3" />
            Toplam
          </div>
          <div className="relative">
            <div className="font-heading text-3xl sm:text-4xl font-light tracking-tighter" data-testid="stat-total">
              <CountUp value={total} />
            </div>
            <div className="text-[10px] text-neutral-400">Üye</div>
          </div>
        </motion.div>

        {STATUSES.map((s, i) => {
          const meta = STATUS_META[s];
          const count = stats?.[s] || 0;
          const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
          return (
            <motion.div
              key={s}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 * (i + 1), ease: "easeOut" }}
              className={`${meta.bg} border ${meta.border} rounded-lg p-3 border-l-4 flex flex-col justify-between min-h-[92px] sm:min-h-[100px]`}
              data-testid={`stat-${meta.key}`}
            >
              <div className="flex items-start gap-1.5">
                <span className={`h-2 w-2 rounded-full ${meta.indicator} mt-1 shrink-0`} />
                <div className={`text-[10px] font-semibold leading-tight ${meta.text}`}>
                  {meta.label}
                </div>
              </div>
              <div>
                <div className={`font-heading text-3xl font-light tracking-tighter ${meta.text}`}>
                  <CountUp value={count} />
                </div>
                <div className={`text-[10px] ${meta.text} opacity-75`}>
                  %{pct} · {meta.desc}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Chart card */}
      {total > 0 && chartData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.2, ease: "easeOut" }}
          className="order-1 lg:order-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 flex items-start justify-between border-b border-neutral-100 dark:border-neutral-800">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500 dark:text-neutral-400">
                <TrendingUp className="h-3 w-3" />
                Dilim Dağılımı
              </div>
              <div className="font-heading text-xl sm:text-2xl font-semibold tracking-tight mt-0.5 text-neutral-950 dark:text-neutral-50">
                {active ? (
                  <span className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full shrink-0" style={{ background: active.color }} />
                    <span className="truncate">{active.label}</span>
                  </span>
                ) : (
                  "Genel Dağılım"
                )}
              </div>
            </div>
            <div className="text-right shrink-0 pl-3">
              <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500 dark:text-neutral-400">
                Toplam
              </div>
              <div className="font-heading text-xl sm:text-2xl font-semibold text-neutral-950 dark:text-neutral-50 font-mono">
                {total}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 p-4 sm:p-5">
            {/* Donut */}
            <div className="lg:col-span-2 relative flex flex-col items-center justify-center min-h-[320px] sm:min-h-[380px]">
              {/* Soft glow behind donut — pre-colored circles cross-faded via opacity only.
                  (Animating opacity avoids the square-frame repaint artifact that
                  animating background-color on a blurred layer causes on mobile.) */}
              <div className="absolute inset-0 pointer-events-none" aria-hidden>
                <motion.div
                  className="absolute left-1/2 top-1/2 h-56 w-56 sm:h-72 sm:w-72 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl transform-gpu"
                  style={{ backgroundColor: "#0A0A0A" }}
                  animate={{ opacity: active ? 0 : 0.10 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
                {chartData.map((entry, i) => (
                  <motion.div
                    key={`glow-${i}`}
                    className="absolute left-1/2 top-1/2 h-56 w-56 sm:h-72 sm:w-72 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl transform-gpu"
                    style={{ backgroundColor: entry.color }}
                    animate={{ opacity: activeIndex === i ? 0.30 : 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  />
                ))}
              </div>
              <div className="relative h-[320px] sm:h-[380px] w-full max-w-[380px] mx-auto" data-testid="pie-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 16, right: 16, bottom: 16, left: 16 }}>
                    <defs>
                      {chartData.map((entry, i) => (
                        <radialGradient key={`grad-${i}`} id={`grad-${i}`} cx="50%" cy="50%" r="65%">
                          <stop offset="55%" stopColor={entry.color} stopOpacity={1} />
                          <stop offset="100%" stopColor={entry.color} stopOpacity={0.78} />
                        </radialGradient>
                      ))}
                    </defs>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius="52%"
                      outerRadius="82%"
                      paddingAngle={2}
                      cornerRadius={5}
                      startAngle={90}
                      endAngle={-270}
                      strokeWidth={0}
                      activeIndex={activeIndex ?? undefined}
                      activeShape={renderActiveShape}
                      onMouseEnter={(_, i) => setActiveIndex(i)}
                      onMouseLeave={() => setActiveIndex(null)}
                      onClick={(_, i) => setActiveIndex((prev) => (prev === i ? null : i))}
                      isAnimationActive
                      animationBegin={150}
                      animationDuration={800}
                      animationEasing="ease-out"
                    >
                      {chartData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={`url(#grad-${i})`}
                          style={{
                            filter:
                              activeIndex === i
                                ? `drop-shadow(0 4px 10px ${entry.color}66)`
                                : "none",
                            transition: "filter 250ms ease",
                            cursor: "pointer",
                            outline: "none",
                          }}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>

                {/* Center label */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeIndex ?? "total"}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="text-center px-2 max-w-[58%] mx-auto"
                    >
                      {active ? (
                        <>
                          <div className="font-heading text-4xl font-light tracking-tighter text-neutral-950 dark:text-neutral-50">
                            {active.value}
                          </div>
                          <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-neutral-500 dark:text-neutral-400 mt-0.5">
                            %{((active.value / total) * 100).toFixed(1)}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-heading text-4xl font-light tracking-tighter text-neutral-950 dark:text-neutral-50">
                            {total}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500 dark:text-neutral-400 mt-0.5">
                            Toplam Üye
                          </div>
                        </>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

            </div>

            {/* Legend with progress bars */}
            <div className="lg:col-span-3 space-y-2.5" data-testid="chart-legend">
              {CHART_ORDER.map((s, i) => {
                const meta = STATUS_META[s];
                const count = stats?.[s] || 0;
                const pctNum = total > 0 ? (count / total) * 100 : 0;
                const pct = pctNum.toFixed(1);
                const chartIdx = chartData.findIndex((c) => c.name === s);
                const isActive = activeIndex === chartIdx;
                return (
                  <motion.button
                    type="button"
                    key={s}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, delay: 0.35 + i * 0.05 }}
                    onMouseEnter={() => chartIdx >= 0 && setActiveIndex(chartIdx)}
                    onMouseLeave={() => setActiveIndex(null)}
                    onFocus={() => chartIdx >= 0 && setActiveIndex(chartIdx)}
                    onBlur={() => setActiveIndex(null)}
                    onClick={() =>
                      chartIdx >= 0 &&
                      setActiveIndex((prev) => (prev === chartIdx ? null : chartIdx))
                    }
                    className={`w-full text-left group relative rounded-lg border p-3 transition-all ${
                      isActive
                        ? "border-neutral-900 dark:border-neutral-100 bg-neutral-50 dark:bg-neutral-800 shadow-sm"
                        : "border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:border-neutral-300 dark:hover:border-neutral-700"
                    }`}
                    data-testid={`legend-${meta.key}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span
                          className="h-8 w-2 rounded-full shrink-0 transition-all"
                          style={{
                            background: meta.dot,
                            boxShadow: isActive ? `0 0 12px ${meta.dot}` : "none",
                          }}
                        />
                        <div className="min-w-0">
                          <div className="font-heading font-semibold text-sm text-neutral-950 dark:text-neutral-100 leading-tight">
                            {meta.label}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.05em] text-neutral-500 dark:text-neutral-400 font-medium">
                            {meta.desc}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-heading text-xl font-semibold text-neutral-950 dark:text-neutral-100 leading-none font-mono">
                          <CountUp value={count} />
                        </div>
                        <div className="text-[10px] text-neutral-500 dark:text-neutral-400 font-mono mt-0.5">
                          %{pct}
                        </div>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2.5 h-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pctNum}%` }}
                        transition={{ duration: 0.9, delay: 0.4 + i * 0.06, ease: "easeOut" }}
                        className="h-full rounded-full"
                        style={{
                          background: meta.dot,
                          boxShadow: isActive ? `0 0 8px ${meta.dot}` : "none",
                        }}
                      />
                    </div>

                    {/* Şirket türü kırılımı: kaçı şirket, kaçı şahıs */}
                    {count > 0 && !meta.hideEntitySplit && (
                      <div
                        className="mt-2 flex items-center gap-3"
                        data-testid={`legend-types-${meta.key}`}
                      >
                        {ENTITY_TYPES.map((t) => {
                          const em = ENTITY_META[t];
                          const adet = kirilim(stats, s)[t] || 0;
                          return (
                            <span
                              key={t}
                              className={`inline-flex items-center gap-1.5 text-[10px] font-medium ${
                                adet > 0 ? em.text : "text-neutral-300 dark:text-neutral-700"
                              }`}
                              title={em.vote}
                            >
                              <span
                                className="h-1.5 w-1.5 rounded-full shrink-0"
                                style={{ background: adet > 0 ? em.dot : "currentColor" }}
                              />
                              <span className="font-mono font-semibold">{adet}</span>
                              <span className="uppercase tracking-[0.06em]">{em.label}</span>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
